<script setup lang="tsx" generic="TableData extends Api.Common.CommonRecord">
import type { Ref } from 'vue';
import { computed, nextTick, onMounted, ref, toValue, watch } from 'vue';
import type { Api } from '@/typings/api';
import type { BasicTable, BasicTableExpose, BasicTableProps } from '@/typings/basic-table';
import type { BasicDrawerFormProps, BasicFormExpose } from '@/typings/basic-form';
import type { MixedTableFormExpose } from '@/typings/mixed-table-form';

export type MixedFormProps<T extends Api.Common.CommonRecord> = Omit<
  BasicDrawerFormProps<T>,
  'show' | 'operation' | 'edit-data'
>;

export interface MixedTableFormProps<T extends Api.Common.CommonRecord> {
  /**
   * 级联模式
   *
   * 开启此模式后，表单的表单数据将直接绑定到表格的数据中，不再调用API提交
   */
  cascadeMode?: boolean;
  /** 初始化新建数据 */
  initNewData?: () => T;
  /** 表格配置 */
  tableProps?: BasicTableProps<T>;
  /** 表单配置 */
  formProps?: MixedFormProps<T>;
  disabled?: boolean;
}
defineOptions({
  /** 组合列表和表单，实现可编辑的列表功能 */
  name: 'MixedTableForm',
  inheritAttrs: false
});

const props = defineProps<MixedTableFormProps<TableData>>();

const expandedRowKeys = defineModel<string[]>('expandedRowKeys', {
  default: () => []
});

const emit = defineEmits<{
  closeForm: [];
  mounted: [
    {
      tableRef: Ref<BasicTableExpose<TableData> | null>;
      formRef: Ref<BasicFormExpose<TableData> | null>;
    }
  ];
}>();

const tableRef = ref<BasicTableExpose<TableData> | null>(null);
const formRef = ref<BasicFormExpose<TableData> | null>(null);

// 过滤掉 onSave/onUpdate，防止 v-bind 传递到 props
const formPropsWithoutEvents = computed<MixedFormProps<TableData>>(() => {
  if (!props.formProps) return {} as MixedFormProps<TableData>;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { onSave, onUpdate, ...rest } = props.formProps;
  return rest;
});

watch(
  () => formRef.value?.show,
  val => {
    if (!val) {
      emit('closeForm');
    }
  }
);

function saveField(model: TableData) {
  if (tableRef.value?.data) {
    tableRef.value.data.push(model);
  }
  return Promise.resolve({ data: {}, error: null });
}

function updateField(_model: any) {
  return Promise.resolve({ data: {}, error: null });
}

function deleteField(id: string) {
  if (tableRef.value?.data) {
    tableRef.value.data.splice(
      tableRef.value.data.findIndex(item => item.id === id),
      1
    );
  }
  return Promise.resolve({ data: {}, error: null });
}

function afterSave(...args: any[]) {
  if (!props.cascadeMode) {
    tableRef.value?.getData();
  }
  if (typeof props.formProps?.onSave === 'function') {
    props.formProps.onSave(...args);
  }
}

function afterUpdate(...args: any[]) {
  if (!props.cascadeMode) {
    tableRef.value?.getData();
  }
  if (typeof props.formProps?.onUpdate === 'function') {
    props.formProps.onUpdate(...args);
  }
}
function afterDelete(_id: string | string[]) {
  if (!props.cascadeMode) {
    tableRef.value?.getData();
  }
  if (typeof props.tableProps?.onDelete === 'function') {
    props.tableProps.onDelete(_id as string);
  }
  if (typeof props.tableProps?.onDeleteBatch === 'function') {
    props.tableProps.onDeleteBatch(_id as string[]);
  }
}

function importSuccess(param: { tableData: Ref<TableData[]>; importData: any[] }) {
  if (typeof props.tableProps?.onImportSuccess === 'function') {
    props.tableProps.onImportSuccess(param);
  }
}

function importFail(e?: Error) {
  if (typeof props.tableProps?.onImportFail === 'function') {
    props.tableProps.onImportFail(e);
  }
}

function getTableOperation(
  tableOperation: BasicTable.TableOperation[] | undefined
): BasicTable.TableOperation[] | undefined {
  if (tableOperation) {
    const addIndex = tableOperation.indexOf('add');
    if (addIndex > -1 && props.disabled) {
      return tableOperation.filter(item => item !== 'add');
    }
  }
  return tableOperation;
}

const tableData = ref<TableData[]>();
async function getTableData() {
  if (props.tableProps && !props.tableProps.data && props.tableProps.fetchFn) {
    if (tableRef.value) {
      tableRef.value.startLoading();
      await tableRef.value.getData();
      if (tableRef.value.data) {
        tableData.value = toValue(tableRef.value.data) as TableData[];
      }
      tableRef.value.endLoading();
    }
  }
}

const finalTableProps = computed(() => {
  if (props.tableProps) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { onImportSuccess, onImportFail, onDelete, onDeleteBatch, ...tableProps } = props.tableProps;
    if (tableData.value) {
      tableProps.data = tableData.value as TableData[];
      delete tableProps.fetchFn;
    }
    return tableProps;
  }
  return {} as BasicTableProps<TableData>;
});

/** 暴露给父组件 */
defineExpose<MixedTableFormExpose<TableData>>({
  tableRef: tableRef as Ref<BasicTableExpose<TableData> | null>,
  formRef: formRef as Ref<BasicFormExpose<TableData> | null>
});

onMounted(async () => {
  if (props.cascadeMode) {
    await getTableData();
    nextTick(() => {
      tableRef.value?.getData();
    });
  }
  emit('mounted', {
    tableRef: tableRef as Ref<BasicTableExpose<TableData> | null>,
    formRef: formRef as Ref<BasicFormExpose<TableData> | null>
  });
});
</script>

<template>
  <BasicTable
    v-if="tableProps !== undefined"
    ref="tableRef"
    v-bind="finalTableProps"
    v-model:expanded-row-keys="expandedRowKeys"
    :table-style="{ minHeight: '300px', ...tableProps?.tableStyle }"
    :delete-fn="cascadeMode ? deleteField : tableProps?.deleteFn"
    :table-operation="getTableOperation(toValue(tableProps?.tableOperation))"
    @delete="afterDelete"
    @delete-batch="afterDelete"
    @import-success="importSuccess"
    @import-fail="importFail"
  >
    <template #header-extra>
      <slot name="header-extra"></slot>
    </template>
  </BasicTable>
  <BasicDrawerForm
    v-if="formProps && tableRef != null"
    ref="formRef"
    v-model:show="tableRef.modalVisible"
    :operation="tableRef.operation"
    :edit-data="tableRef.operation === 'add' ? initNewData ? initNewData() : null : (tableRef.currentRow as TableData)"
    v-bind="formPropsWithoutEvents"
    :save-fn="cascadeMode ? saveField : formProps?.saveFn"
    :update-fn="cascadeMode ? updateField : formProps?.updateFn"
    @save="afterSave"
    @update="afterUpdate"
  >
    <template v-for="(_slot, name) in $slots" #[name]="data" :key="name">
      <slot :name="name" v-bind="data"></slot>
    </template>
  </BasicDrawerForm>
</template>

<style scoped></style>
